'use client';

import '@/polyfills';

import React, { Suspense, useEffect, useState } from 'react';

import { ApolloProvider } from '@apollo/client';
import { QueryClientProvider } from '@tanstack/react-query';

import { AccessibilityProvider } from '@/components/Accessibility/AccessibilityContext';
import Analytics from '@/components/Analytics/AnalyticsTracker';
import DeferredVendorScripts from '@/components/Analytics/DeferredVendorScripts';
import { GlobalConfigProvider } from '@/components/GlobalContext';
import { SpinnerProvider } from '@/components/SpinnerContext';

import { ConfigProvider } from '@/appconfig/ConfigContext';
import { AppResolution, AppResolutionType } from '@/enums';
import '@/public/css/globals.css';
import { AuthProvider } from '@/services/auth/authContext';
import { apolloClient } from '@/services/graphQL';
import { Language } from '@/services/graphQL/api/generated/types_web';
import { queryClient } from '@/services/graphQL/queryClient';
import { SmartIdProvider } from '@/services/userEngagementSuite/SmartIdContext';
import { getSystem } from '@/system';
import { normalizeDeviceLanguage } from '@/utils/normalizeDeviceLanguage';
import { setRootFontSizeCache } from '@/utils/remToPixels';

import App from './app';

// Mapped, not derived, so an unknown env emits nothing rather than a wrong hint.
const IMAGE_ORIGIN_BY_RTE_HOST: Record<string, string> = {
  'rte-api.bellmedia.ca': 'https://images.rte.bellmedia.ca',
  'stg.rte-api.bellmedia.ca': 'https://images.stg.rte.bellmedia.ca',
};

// new URL() throws on the relative path some envs use for the account API.
const absoluteOrigin = (url: string | undefined) =>
  url?.startsWith('https://') ? new URL(url).origin : undefined;

const rteUrl = process.env.NEXT_PUBLIC_RTE_URL;
const rteHostname = rteUrl ? new URL(rteUrl).hostname : undefined;

// Image URLs only arrive with the GraphQL response, so a hint is the earliest
// reach. cors must mirror the real fetch or the socket cannot be reused.
const PRECONNECT_ORIGINS: Array<{ href: string; cors: boolean }> = [
  { href: absoluteOrigin(process.env.NEXT_PUBLIC_API_BASE_URL), cors: true },
  { href: rteHostname && `https://${rteHostname}`, cors: true },
  { href: rteHostname && IMAGE_ORIGIN_BY_RTE_HOST[rteHostname], cors: false },
].filter((origin): origin is { href: string; cors: boolean } => !!origin.href);

export default function RootLayout({ children }: { children: React.ReactNode }) {
  const [rootREM, setRootREM] = useState<string | undefined>(undefined);
  const [currentResolution, setCurrentResolution] = useState<
    AppResolutionType | undefined
  >(undefined);
  const system = getSystem();

  const language = normalizeDeviceLanguage(
    typeof navigator !== 'undefined' ? navigator.language : Language.En
  );
  const [deviceLanguage, setDeviceLanguage] = useState<Language>(language);

  // Handle browser-specific values in useEffect to avoid hydration mismatches
  useEffect(() => {
    // Use actual screen width to determine the rem base so any device at 720p (1280px)
    // scales correctly without needing a per-environment override.
    // getDeviceRootRem is a last resort for devices that may misreport screen.width.
    const isHd = screen.width === 1280;
    const newRem = system.getDeviceRootRem?.() ?? (isHd ? '10.666px' : '16px');
    setRootFontSizeCache(parseFloat(newRem));
    setRootREM(newRem);
    setCurrentResolution(isHd ? AppResolution.Hd : AppResolution.FullHd);

    system.getDeviceLanguage?.().then((lang) => {
      setDeviceLanguage(normalizeDeviceLanguage(lang));
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={apolloClient}>
        <ConfigProvider>
          <SmartIdProvider>
            <GlobalConfigProvider
              language={deviceLanguage}
              currentResolution={currentResolution}
            >
              <AuthProvider>
                <Analytics>
                  <html
                    lang={deviceLanguage.toLowerCase()}
                    style={rootREM ? { fontSize: rootREM } : undefined}
                  >
                    <head>
                      {PRECONNECT_ORIGINS.map(({ href, cors }) => (
                        <link
                          key={href}
                          rel="preconnect"
                          href={href}
                          crossOrigin={cors ? '' : undefined}
                        />
                      ))}
                      <meta
                        name="description"
                        content="Crave - The best in streaming entertainment"
                      />
                      <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1"
                      />
                      <title>Screen</title>
                      {/* Empty data URI: suppresses the /favicon.ico 404 on every start. */}
                      <link rel="icon" href="data:," />
                      <script
                        dangerouslySetInnerHTML={{
                          __html: `!function(t){function e(){var e=this||self;e.globalThis=e,delete t.prototype._T_}"object"!=typeof globalThis&&(this?e():(t.defineProperty(t.prototype,"_T_",{configurable:!0,get:e}),_T_))}(Object);`,
                        }}
                      />
                      <script
                        dangerouslySetInnerHTML={{
                          __html: `+function(a,p,P,b,y){a.braze={};a.brazeQueue=[];for(var s="BrazeSdkMetadata DeviceProperties Card Card.prototype.dismissCard Card.prototype.removeAllSubscriptions Card.prototype.removeSubscription Card.prototype.subscribeToClickedEvent Card.prototype.subscribeToDismissedEvent Card.fromContentCardsJson ImageOnly CaptionedImage ClassicCard ControlCard ContentCards ContentCards.prototype.getUnviewedCardCount Feed Feed.prototype.getUnreadCardCount ControlMessage InAppMessage InAppMessage.SlideFrom InAppMessage.ClickAction InAppMessage.DismissType InAppMessage.OpenTarget InAppMessage.ImageStyle InAppMessage.Orientation InAppMessage.TextAlignment InAppMessage.CropType InAppMessage.prototype.closeMessage InAppMessage.prototype.removeAllSubscriptions InAppMessage.prototype.removeSubscription InAppMessage.prototype.subscribeToClickedEvent InAppMessage.prototype.subscribeToDismissedEvent InAppMessage.fromJson FullScreenMessage ModalMessage HtmlMessage SlideUpMessage User User.Genders User.NotificationSubscriptionTypes User.prototype.addAlias User.prototype.addToCustomAttributeArray User.prototype.addToSubscriptionGroup User.prototype.getUserId User.prototype.getUserId User.prototype.incrementCustomUserAttribute User.prototype.removeFromCustomAttributeArray User.prototype.removeFromSubscriptionGroup User.prototype.setCountry User.prototype.setCustomLocationAttribute User.prototype.setCustomUserAttribute User.prototype.setDateOfBirth User.prototype.setEmail User.prototype.setEmailNotificationSubscriptionType User.prototype.setFirstName User.prototype.setGender User.prototype.setHomeCity User.prototype.setLanguage User.prototype.setLastKnownLocation User.prototype.setLastName User.prototype.setPhoneNumber User.prototype.setPushNotificationSubscriptionType User.prototype.setLineId InAppMessageButton InAppMessageButton.prototype.removeAllSubscriptions InAppMessageButton.prototype.removeSubscription InAppMessageButton.prototype.subscribeToClickedEvent FeatureFlag FeatureFlag.prototype.getStringProperty FeatureFlag.prototype.getNumberProperty FeatureFlag.prototype.getBooleanProperty FeatureFlag.prototype.getImageProperty FeatureFlag.prototype.getJsonProperty FeatureFlag.prototype.getTimestampProperty Banner automaticallyShowInAppMessages destroyFeed hideContentCards showContentCards showFeed showInAppMessage deferInAppMessage toggleContentCards toggleFeed changeUser destroy getDeviceId getDeviceId initialize isPushBlocked isPushPermissionGranted isPushSupported logCardClick logCardDismissal logCardImpressions logContentCardImpressions logContentCardClick logCustomEvent logFeedDisplayed logInAppMessageButtonClick logInAppMessageClick logInAppMessageHtmlClick logInAppMessageImpression logPurchase openSession requestPushPermission removeAllSubscriptions removeSubscription requestContentCardsRefresh requestFeedRefresh refreshFeatureFlags requestImmediateDataFlush enableSDK isDisabled setLogger setSdkAuthenticationSignature addSdkMetadata disableSDK subscribeToContentCardsUpdates subscribeToFeedUpdates subscribeToInAppMessage subscribeToSdkAuthenticationFailures toggleLogging unregisterPush wipeData handleBrazeAction subscribeToFeatureFlagsUpdates getAllFeatureFlags logFeatureFlagImpression requestBannersRefresh insertBanner logBannerClick logBannerImpressions getAllBanners subscribeToBannersUpdates isInitialized".split(" "),i=0;i<s.length;i++){for(var m=s[i],k=a.braze,l=m.split("."),j=0;j<l.length-1;j++)k=k[l[j]];k[l[j]]=(new Function("return function "+m.replace(/\\./g,"_")+"(){window.brazeQueue.push(arguments); return true}"))()}window.braze.getDeferredInAppMessage=function(){return new window.braze.InAppMessage};window.braze.getCachedContentCards=function(){return new window.braze.ContentCards};window.braze.getCachedFeed=function(){return new window.braze.Feed};window.braze.getUser=function(){return new window.braze.User};window.braze.getFeatureFlag=function(){return new window.braze.FeatureFlag};window.braze.getBanner=function(){return new window.braze.Banner};window.braze.newBannerFromJson=function(){return new window.braze.Banner}}(window,document,'script');`,
                        }}
                      />
                      <script src="https://cdnjs.cloudflare.com/polyfill/v3/polyfill.min.js?version=4.8.0&features=Node.prototype.getRootNode%2CResizeObserver%2CDOMRect"></script>

                      {process.env.NEXT_PUBLIC_PERMUTIVE_WORKSPACE_API_KEY &&
                        process.env.NEXT_PUBLIC_PERMUTIVE_WORKSPACE_ID && (
                          <script
                            dangerouslySetInnerHTML={{
                              __html: `!function(e,o,n,i){if(!e){e=e||{},window.permutive=e,e.q=[];var t=function(){return([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,function(e){return(e^(window.crypto||window.msCrypto).getRandomValues(new Uint8Array(1))[0]&15>>e/4).toString(16)})};e.config=i||{},e.config.apiKey=o,e.config.workspaceId=n,e.config.environment=e.config.environment||"production",(window.crypto||window.msCrypto)&&(e.config.viewId=t());for(var g=["addon","identify","track","trigger","query","segment","segments","ready","on","once","user","consent"],r=0;r<g.length;r++){var w=g[r];e[w]=function(o){return function(){var n=Array.prototype.slice.call(arguments,0);e.q.push({functionName:o,arguments:n})}}(w)}}}(window.permutive,"${process.env.NEXT_PUBLIC_PERMUTIVE_WORKSPACE_API_KEY}","${process.env.NEXT_PUBLIC_PERMUTIVE_WORKSPACE_ID}",{});window.googletag=window.googletag||{},window.googletag.cmd=window.googletag.cmd||[],window.googletag.cmd.push(function(){if(0===window.googletag.pubads().getTargeting("permutive").length){var e=window.localStorage.getItem("_pdfps");window.googletag.pubads().setTargeting("permutive",e?JSON.parse(e):[]);var o=window.localStorage.getItem("permutive-id");o&&(window.googletag.pubads().setTargeting("puid",o),window.googletag.pubads().setTargeting("ptime",Date.now().toString())),window.permutive.config.viewId&&window.googletag.pubads().setTargeting("prmtvvid",window.permutive.config.viewId),window.permutive.config.workspaceId&&window.googletag.pubads().setTargeting("prmtvwid",window.permutive.config.workspaceId)}});permutive.addon('web', {});`,
                            }}
                          />
                        )}

                      {/* Deferred to keep it off the critical render path */}
                      <script
                        defer
                        src="https://asset.entpay.9c9media.ca/comscore/comscore.v7.15.0.js"
                      ></script>

                      {/* The Braze and Permutive bundles are loaded by
                          DeferredVendorScripts; only their stubs belong here. */}
                    </head>
                    <body className="font-sans" suppressHydrationWarning>
                      <AccessibilityProvider>
                        <SpinnerProvider>
                          <Suspense
                            fallback={<div className="w-screen h-screen bg-black" />}
                          >
                            <App>{children}</App>
                          </Suspense>
                        </SpinnerProvider>
                      </AccessibilityProvider>
                      <DeferredVendorScripts />
                      {system.script()}
                    </body>
                  </html>
                </Analytics>
              </AuthProvider>
            </GlobalConfigProvider>
          </SmartIdProvider>
        </ConfigProvider>
      </ApolloProvider>
    </QueryClientProvider>
  );
}
